import classNames from 'classnames';
import UserAvatar, {
  BasicPerson,
} from '@/components/molecules/user-avatar/user-avatar';
import styles from './selected-passengers.module.scss';

export interface SelectedPassengersProps {
  /** The class name to apply */
  className?: string;
  /** The maximum number of passengers to display */
  limit?: number;
  /** Names to display */
  passengers: BasicPerson[];
  /** Indicates the size of the avatar used */
  size?: 'small' | 'medium' | 'large';
}

const SelectedPassengers = ({
  className,
  limit = 4,
  passengers = [],
  size = 'large',
}: SelectedPassengersProps) => {
  if (passengers.length === 0) return;

  const getBasicPerson = (passenger: BasicPerson): BasicPerson => {
    const { firstName, lastName, hasDashedBorder, imageUrl, placeholders } =
      passenger;

    const shouldUsePlaceholders = placeholders && !firstName && !lastName;

    return {
      firstName: shouldUsePlaceholders ? placeholders!.firstName : firstName,
      lastName: shouldUsePlaceholders ? placeholders!.lastName : lastName,
      ...(hasDashedBorder !== undefined && { hasDashedBorder }),
      ...(imageUrl !== undefined && { imageUrl }),
    };
  };

  const getNameDisplay = (passenger: BasicPerson): string => {
    const { firstName, lastName, placeholders } = passenger;

    if (placeholders && !firstName && !lastName) {
      return `${placeholders.firstName} ${placeholders.lastName}`;
    }

    return `${firstName} ${lastName}`;
  };

  const visiblePassengers = passengers.slice(0, limit);
  const overflowPassengers = passengers.slice(limit);
  const overflowText = overflowPassengers
    .map(passenger => getNameDisplay(passenger))
    .join(', ');

  return (
    <div className={classNames(styles.component, className)}>
      {visiblePassengers.map((passenger, passengerIndex) => (
        <UserAvatar
          key={passengerIndex}
          className={styles.userAvatar}
          passenger={getBasicPerson(passenger)}
          size={size}
        />
      ))}

      {overflowPassengers.length > 0 && (
        <UserAvatar
          className={styles.userAvatar}
          overflowText={overflowText}
          passenger={{
            firstName: '+',
            lastName: `${overflowPassengers.length}`,
            hasDashedBorder: overflowPassengers.some(
              passenger => passenger.hasDashedBorder,
            ),
          }}
          size={size}
        />
      )}
    </div>
  );
};

SelectedPassengers.displayName = 'SelectedPassengers';

export default SelectedPassengers;
